import { useEffect, useId, useMemo, useRef, useState } from 'react';
import styles from './QuartierCombobox.module.css';

export interface QuartierComboboxProps {
  value: string;
  onChange: (next: string) => void;
  options: readonly string[];
  placeholder: string;
  /** Accessible name for the options list (the input is named by its visible label). */
  listLabel: string;
  /** Wires the input to an external <label>, hint and error (see FormField). */
  inputId?: string | undefined;
  ariaDescribedBy?: string | undefined;
  inputClassName?: string | undefined;
}

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

export function QuartierCombobox({
  value,
  onChange,
  options,
  placeholder,
  listLabel,
  inputId,
  ariaDescribedBy,
  inputClassName,
}: QuartierComboboxProps) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listboxId = useId();

  const filtered = useMemo(() => {
    const q = normalize(value.trim());
    if (!q) return options;
    return options.filter((o) => normalize(o).includes(q));
  }, [options, value]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open || activeIdx < 0 || !listRef.current) return;
    const el = listRef.current.children[activeIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  const commit = (next: string) => {
    onChange(next);
    setOpen(false);
    setActiveIdx(-1);
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && activeIdx >= 0 && filtered[activeIdx]) {
        e.preventDefault();
        commit(filtered[activeIdx]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={styles.wrap}>
      <input
        id={inputId}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={open && activeIdx >= 0 ? `${listboxId}-opt-${activeIdx}` : undefined}
        className={inputClassName}
        placeholder={placeholder}
        aria-describedby={ariaDescribedBy}
        value={value}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="words"
        spellCheck={false}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActiveIdx(-1);
        }}
        onFocus={() => setOpen(true)}
        // Tabbing away closes the list; option clicks preventDefault on
        // mousedown, so choosing one never blurs the input first.
        onBlur={() => setOpen(false)}
        onKeyDown={onKey}
      />
      {open && filtered.length > 0 && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-label={listLabel}
          className={styles.list}
        >
          {filtered.map((opt, i) => (
            <li
              key={opt}
              id={`${listboxId}-opt-${i}`}
              role="option"
              aria-selected={i === activeIdx}
              className={`${styles.opt} ${i === activeIdx ? styles.active : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                commit(opt);
              }}
              onMouseEnter={() => setActiveIdx(i)}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
